import { BarcodeOutlined, DownloadOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import type { FieldMeta } from "@wms/domain";
import { Alert, Button, Empty, Modal, Space, Spin, Tag, Tooltip, Typography } from "antd";
import JsBarcode from "jsbarcode";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, errorMessage, type Page } from "../api/client";
import { formatQty } from "../api/format";
import { useAuthStore } from "../auth/store";
import "./barcode.css";

type Row = Record<string, unknown> & { id: string };

/** What a terminal reads by a code at the steps of a work: a product (its barcodes, GTIN, code), a place (cell, pallet, gate), a cargo unit. */
export const CODE_ENTITIES = new Set(["storage_objects", "locations", "cargo_units"]);

/** In the list of such objects themselves the button stands at their own code. */
const OWN_CODE_FIELD: Record<string, string> = { storage_objects: "code", locations: "code", cargo_units: "number" };

interface Code {
  value: string;
  title: string;
  hint?: string;
  /** the package of the line the button was pressed in */
  current?: boolean;
}

interface Target {
  entity: string;
  id: string;
  packageId?: string | null;
  /** the code itself (a row of the barcode list): nothing to look up */
  value?: string;
  /** UXW-33: the base unit of the product («шт»), so a package reads «в упаковке 50 шт» */
  unit?: string | null;
}

/** UXW-33: what a registered code is for — the source of the barcode in the words of a worker. */
const SOURCE_WORDS: Record<string, string> = {
  manufacturer: "код производителя",
  supplier: "код поставщика",
  wms: "внутренний код MY WMS",
};

interface Codes {
  label: string;
  codes: Code[];
  /** UXW-34: a product without a registered barcode and without a GTIN — only its own code is shown */
  notAssigned?: boolean;
  /** UXW-33: packages that have no barcode of their own, with their quantity in the base unit */
  bare?: string[];
  /** the role may not read the barcode list: «не назначен» is not claimed, the GTIN and the code are shown */
  restricted?: boolean;
}

/** The object behind a table cell whose code a terminal scans, or null (the cell gets no button). */
export function codeTarget(entity: string, field: FieldMeta, value: unknown, record: Row): Target | null {
  if (value === null || value === undefined || value === "") return null;
  if (field.type === "ref" && field.ref && CODE_ENTITIES.has(field.ref)) {
    const packageId = field.ref === "storage_objects" && typeof record.package_id === "string" ? record.package_id : null;
    return { entity: field.ref, id: String(value), packageId };
  }
  if (OWN_CODE_FIELD[entity] === field.name) return { entity, id: record.id };
  if (entity === "barcodes" && field.name === "barcode") return { entity, id: record.id, value: String(value) };
  return null;
}

function eanValid(digits: string): boolean {
  const n = digits.split("").map(Number);
  const check = n.pop()!;
  const sum = n.reverse().reduce((s, d, i) => s + d * (i % 2 === 0 ? 3 : 1), 0);
  return (10 - (sum % 10)) % 10 === check;
}

/** EAN-13 / EAN-8 for a maker's barcode, Code 128 for any other code: the terminal's scanner and camera read both. */
export function symbology(value: string): "EAN13" | "EAN8" | "CODE128" {
  if (/^\d{13}$/.test(value) && eanValid(value)) return "EAN13";
  if (/^\d{8}$/.test(value) && eanValid(value)) return "EAN8";
  return "CODE128";
}

/** Code 128 carries Latin letters, digits and signs only: an address in Cyrillic has no barcode picture. */
export function drawable(value: string): boolean {
  return /^[\x20-\x7e]+$/.test(value);
}

function draw(target: SVGSVGElement | HTMLCanvasElement, value: string, large: boolean): boolean {
  if (!drawable(value)) return false;
  let ok = true;
  try {
    // black on white whatever the theme: a scanner needs the contrast and the quiet zone around the bars
    JsBarcode(target, value, {
      format: symbology(value),
      width: large ? 4 : 2,
      height: large ? 160 : 80,
      margin: large ? 32 : 14,
      fontSize: large ? 30 : 16,
      background: "#ffffff",
      lineColor: "#000000",
      valid: (valid) => {
        ok = valid;
      },
    });
  } catch {
    ok = false;
  }
  return ok;
}

function downloadPng(value: string) {
  const canvas = document.createElement("canvas");
  if (!draw(canvas, value, true)) return;
  canvas.toBlob((blob) => {
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${value.replace(/[^\w.-]+/g, "_")}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  }, "image/png");
}

function CodeCard({ code }: { code: Code }) {
  const ref = useRef<SVGSVGElement>(null);
  const [ok, setOk] = useState(() => drawable(code.value));
  useEffect(() => {
    if (ref.current) setOk(draw(ref.current, code.value, false));
  }, [code.value]);
  return (
    <div className={`wms-code-card${code.current ? " wms-code-card-current" : ""}`} data-testid="barcode-card">
      <Space size={6} wrap>
        <Typography.Text strong>{code.title}</Typography.Text>
        {code.current && <Tag color="blue">упаковка этой строки</Tag>}
      </Space>
      {code.hint && <Typography.Text type="secondary">{code.hint}</Typography.Text>}
      {ok ? (
        <div className="wms-code-image">
          <svg ref={ref} role="img" aria-label={`Штрихкод ${code.value}`} />
        </div>
      ) : (
        <Alert
          type="warning"
          showIcon
          message={`«${code.value}» нельзя нарисовать штрихкодом: в нём есть буквы не латиницей`}
          description="Терминал примет этот код, если ввести его вручную. Чтобы сканировать, задайте месту или товару штрихкод из латиницы и цифр."
        />
      )}
      <Button size="small" icon={<DownloadOutlined />} disabled={!ok} onClick={() => downloadPng(code.value)}>
        Скачать PNG
      </Button>
    </div>
  );
}

function packageHint(pkg: Row | undefined, unit?: string | null): string | undefined {
  const ratio = Number(pkg?.ratio ?? 1);
  return pkg && ratio !== 1 ? `в упаковке ${formatQty(pkg.ratio)} ${unit || "баз. ед."}` : undefined;
}

async function loadCodes({ entity, id, packageId, value, unit }: Target): Promise<Codes> {
  if (value) return { label: value, codes: [{ value, title: "Штрихкод" }] };
  if (entity === "locations") {
    const place = await api.get<Row>(`/locations/${id}`);
    const value = String(place.barcode || place.code);
    return {
      label: `Место ${place.code}`,
      codes: [{ value, title: "Штрихкод места", hint: value !== place.code ? `адрес ${place.code}` : undefined }],
    };
  }
  if (entity === "cargo_units") {
    const unit = await api.get<Row>(`/cargo_units/${id}`);
    return { label: `Грузовое место ${unit.number}`, codes: [{ value: String(unit.number), title: "SSCC грузового места" }] };
  }
  const product = await api.get<Row>(`/storage_objects/${id}`);
  const filter = { field: "storage_object_id", op: "eq", value: id };
  // a role without the barcode or package lists still gets the code of the product
  const [barcodes, packages] = await Promise.all([
    api.get<Page<Row>>("/barcodes", { filter, size: 100 }).catch(() => null),
    api.get<Page<Row>>("/packages", { filter, size: 100 }).catch(() => null),
  ]);
  const packageOf = new Map((packages?.items ?? []).map((p) => [p.id, p]));
  const codes: Code[] = [];
  for (const row of barcodes?.items ?? []) {
    if (row.is_active === false || !row.barcode) continue;
    const pkg = typeof row.package_id === "string" ? packageOf.get(row.package_id) : undefined;
    // UXW-33: the package with its quantity in the base unit and what the code is for; a package code is never
    // presented as the code of one item
    const hint = [packageHint(pkg, unit), SOURCE_WORDS[String(row.source ?? "")]].filter(Boolean).join(" · ");
    codes.push({
      value: String(row.barcode),
      title: pkg ? `Упаковка «${pkg.name}»` : "Штрихкод товара",
      hint: hint || undefined,
      current: !!packageId && row.package_id === packageId,
    });
  }
  const registered = codes.length > 0 || !!product.gtin;
  if (product.gtin) codes.push({ value: String(product.gtin), title: "GTIN товара" });
  codes.push({ value: String(product.code), title: "Код товара", hint: "терминал узнаёт товар и по коду" });
  const seen = new Set<string>();
  const unique = codes.filter((c) => !seen.has(c.value) && seen.add(c.value));
  unique.sort((a, b) => Number(!!b.current) - Number(!!a.current));
  const coded = new Set((barcodes?.items ?? []).filter((r) => r.is_active !== false).map((r) => r.package_id));
  const bare = (packages?.items ?? [])
    .filter((p) => !coded.has(p.id) && Number(p.ratio ?? 1) !== 1)
    .map((p) => `${p.name} — ${formatQty(p.ratio)} ${unit || "баз. ед."}`);
  return {
    label: `${product.code} — ${product.name}`,
    codes: unique,
    notAssigned: barcodes !== null && !registered,
    bare,
    restricted: barcodes === null,
  };
}

/** UXW-34: no barcode of the product — said so, with the way to the card where a code is added (by rights). */
function NotAssigned({ id }: { id: string }) {
  const can = useAuthStore((s) => s.can);
  return (
    <Alert
      type="warning"
      showIcon
      style={{ marginBottom: 12 }}
      data-testid="barcode-not-assigned"
      message="Штрихкод не назначен"
      description={
        <span>
          У товара нет зарегистрированного штрихкода и GTIN — терминал узнает его по коду товара ниже. Просмотр не создаёт
          новый код.{" "}
          {can("nsi.storage_objects.read") && (
            <Link to={`/storage_objects/${id}`}>Открыть карточку товара: вкладка «Штрихкоды»</Link>
          )}
        </span>
      }
    />
  );
}

function BarcodeModal({ target, onClose }: { target: Target; onClose: () => void }) {
  const codes = useQuery({
    queryKey: ["codes", target.entity, target.id, target.packageId ?? null],
    queryFn: () => loadCodes(target),
    staleTime: 30_000,
    retry: false,
  });
  return (
    <Modal open onCancel={onClose} footer={null} width={560} title={codes.data ? `Штрихкод: ${codes.data.label}` : "Штрихкод"} data-testid="barcode-modal">
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message="Наведите ТСД на экран"
        description="Сканер и камера терминала читают код прямо с монитора. Не читается — прибавьте яркость или скачайте PNG и распечатайте."
      />
      {codes.isLoading ? (
        <Spin />
      ) : codes.isError ? (
        <Alert type="error" showIcon message={errorMessage(codes.error)} />
      ) : !codes.data?.codes.length ? (
        <Empty description="Кода нет" />
      ) : (
        <>
          {codes.data.notAssigned && <NotAssigned id={target.id} />}
          {codes.data.restricted && (
            <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
              Список штрихкодов товара недоступен вашей роли — показаны GTIN и код товара.
            </Typography.Paragraph>
          )}
          <div className="wms-code-list">
            {codes.data.codes.map((c) => (
              <CodeCard key={c.value} code={c} />
            ))}
          </div>
          {!!codes.data.bare?.length && (
            <Typography.Paragraph type="secondary" style={{ marginTop: 12, marginBottom: 0 }} data-testid="barcode-bare-packages">
              Упаковки без своего штрихкода: {codes.data.bare.join("; ")}.
            </Typography.Paragraph>
          )}
        </>
      )}
    </Modal>
  );
}

/**
 * «ШК для ТСД» (29.10.2026): at every product, place or cargo unit of a list — the lines of a document, the tasks,
 * the cargo units — a button opens the very code the terminal expects at that step, large enough to scan from the
 * screen, with a PNG to download.
 */
export function BarcodeButton({ label, tooltip, ...target }: Target & { label?: string; tooltip?: string }) {
  const [open, setOpen] = useState(false);
  return (
    // a list row opens its card on a click: neither the button nor its dialog (a portal of this span) let it through;
    // UXW-32: nor does Enter/Space on the button — a row that opens by the keyboard listens to keydown
    <span className="wms-code-button" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
      <Tooltip title={tooltip ?? "Штрихкод для ТСД: показать, отсканировать с экрана, скачать"}>
        <Button size="small" type="text" icon={<BarcodeOutlined />} aria-label={label ?? "Штрихкод для ТСД"} data-testid="barcode-button" onClick={() => setOpen(true)} />
      </Tooltip>
      {open && <BarcodeModal target={target} onClose={() => setOpen(false)} />}
    </span>
  );
}
